import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getIncentiveMonitor, markIncentivePaid } from '../../lib/api';
import type { IncentiveSpRow, IncentivePaymentRow } from '../../lib/api';
import { meet } from '../../crm/crmApi';
import type { IncentiveRow, MeetIncentiveData } from '../../crm/crmApi';
import { IncentiveRows } from '../coordinator/SalesMeetings';

function fmtINR(n: number) { return '₹' + n.toLocaleString('en-IN'); }
function fmtDate(s: string | null) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* ── Payment row inside drawer ────────────────────────────────────────────── */
const PayRow: React.FC<{
  p: IncentivePaymentRow;
  adminPassword: string;
  onToggle: (paid: boolean) => void;
}> = ({ p, adminPassword, onToggle }) => {
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    setLoading(true);
    try {
      const res = await markIncentivePaid(adminPassword, p.id, p.incentivePaid);
      onToggle(res.incentivePaid);
      toast.success(res.incentivePaid ? 'Marked as incentive given' : 'Marked as not given');
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className={`flex items-center gap-3 py-3 border-b border-gray-100 last:border-0 transition-opacity ${loading ? 'opacity-50' : ''}`}>
      {/* Status dot */}
      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1"
        style={{ background: p.incentivePaid ? '#16a34a' : '#e5e7eb' }} />
      {/* Student info */}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-gray-800 text-sm truncate">{p.studentName}</p>
        <p className="text-[11px] text-gray-400 truncate">{p.course} · {p.weekLabel}</p>
        {p.incentivePaid && p.incentivePaidAt && (
          <p className="text-[10px] text-green-600 font-semibold">Given on {fmtDate(p.incentivePaidAt)}</p>
        )}
      </div>
      {/* Tier + incentive */}
      <div className="text-right flex-shrink-0">
        <p className="font-black text-sm" style={{ color: p.incentivePaid ? '#16a34a' : '#374151' }}>
          {fmtINR(p.incentive)}
        </p>
        <p className="text-[10px] text-gray-400">{p.tierName} · {p.tierPct}%</p>
      </div>
      {/* Toggle button */}
      <button onClick={toggle} disabled={loading}
        className="flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-xl border-2 transition-all disabled:opacity-50"
        style={p.incentivePaid
          ? { background: '#f0fdf4', color: '#16a34a', borderColor: '#86efac' }
          : { background: '#fafafa', color: '#6b7280', borderColor: '#e5e7eb' }}>
        {loading ? '…' : p.incentivePaid ? '✓ Given' : 'Mark Given'}
      </button>
    </div>
  );
};

/* ── SP drawer ─────────────────────────────────────────────────────────────── */
const SpDrawer: React.FC<{
  row: IncentiveSpRow;
  adminPassword: string;
  onClose: () => void;
  onUpdate: (updatedRow: IncentiveSpRow) => void;
}> = ({ row, adminPassword, onClose, onUpdate }) => {
  const [payments, setPayments] = useState<IncentivePaymentRow[]>(row.payments);
  const [filter, setFilter] = useState<'all' | 'given' | 'pending'>('all');

  const handleToggle = (idx: number, paid: boolean) => {
    const next = payments.map((p, i) => i === idx ? { ...p, incentivePaid: paid, incentivePaidAt: paid ? new Date().toISOString() : null } : p);
    setPayments(next);
    // Recompute totals
    const totalPaid = next.filter(p => p.incentivePaid).reduce((s, p) => s + p.incentive, 0);
    onUpdate({ ...row, payments: next, totalPaid, outstanding: row.totalEarned - totalPaid });
  };

  const shown = payments.filter(p =>
    filter === 'all' ? true : filter === 'given' ? p.incentivePaid : !p.incentivePaid
  );

  const totalPaidNow = payments.filter(p => p.incentivePaid).reduce((s, p) => s + p.incentive, 0);
  const outstanding  = row.totalEarned - totalPaidNow;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-md bg-white shadow-2xl"
        style={{ animation: 'imSlideIn .3s cubic-bezier(.32,.72,0,1) both' }}>

        {/* Header */}
        <div className="flex-shrink-0 px-5 pt-5 pb-4" style={{ background: 'linear-gradient(135deg,#1e1433,#312741)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg"
                style={{ background: 'linear-gradient(135deg,#F0B429,#f59e0b)', color: '#312741' }}>
                {row.salesPerson.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-white font-black text-base">{row.salesPerson}</p>
                <p className="text-white/50 text-xs">{payments.length} Fully Paid students</p>
              </div>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-white text-xl leading-none hover:bg-white/25">
              ×
            </button>
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Total Earned', val: row.totalEarned, color: '#fff' },
              { label: 'Given',        val: totalPaidNow,   color: '#86efac' },
              { label: 'Outstanding',  val: outstanding,    color: outstanding > 0 ? '#fde68a' : '#86efac' },
            ].map(s => (
              <div key={s.label} className="bg-white/15 rounded-xl p-2.5 text-center">
                <p className="font-black text-sm" style={{ color: s.color }}>{fmtINR(s.val)}</p>
                <p className="text-white/50 text-[10px] font-semibold mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex-shrink-0 border-b border-gray-200 flex">
          {([['all','All'], ['pending','Pending'], ['given','Given']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              className="flex-1 py-2.5 text-xs font-bold border-b-2 transition-all"
              style={{ color: filter===id?'#7c3aed':'#9ca3af', borderColor: filter===id?'#7c3aed':'transparent' }}>
              {label}
              {id==='pending' && outstanding>0 &&
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px]" style={{ background:'#fef3c7', color:'#d97706' }}>
                  {payments.filter(p=>!p.incentivePaid).length}
                </span>}
            </button>
          ))}
        </div>

        {/* Payment list */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {shown.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-3xl mb-2">🎉</p>
              <p className="text-gray-400 font-semibold text-sm">
                {filter === 'pending' ? 'All incentives given!' : 'No payments here'}
              </p>
            </div>
          ) : (
            shown.map((p, i) => (
              <PayRow key={p.id} p={p} adminPassword={adminPassword}
                onToggle={paid => handleToggle(payments.indexOf(p), paid)} />
            ))
          )}
        </div>
      </div>
    </>
  );
};

/* ── SP summary card ────────────────────────────────────────────────────────── */
const SpCard: React.FC<{
  row: IncentiveSpRow;
  onClick: () => void;
}> = ({ row, onClick }) => {
  const paidPct = row.totalEarned > 0 ? Math.round(row.totalPaid / row.totalEarned * 100) : 0;
  const hasOutstanding = row.outstanding > 0;

  return (
    <button onClick={onClick} className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Progress bar at top */}
      <div className="h-1.5 bg-gray-100">
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${paidPct}%`, background: paidPct === 100 ? '#16a34a' : 'linear-gradient(90deg,#7c3aed,#F0B429)' }} />
      </div>
      <div className="px-4 py-4 flex items-center gap-3">
        {/* Avatar */}
        <div className="w-11 h-11 rounded-xl flex items-center justify-center font-black text-lg flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)', color: '#7c3aed' }}>
          {row.salesPerson.charAt(0).toUpperCase()}
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-black text-gray-800 text-sm">{row.salesPerson}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {row.payments.length} student{row.payments.length !== 1 ? 's' : ''} · {paidPct}% given
          </p>
          {/* Mini progress indicator */}
          <div className="flex items-center gap-1.5 mt-1.5">
            {row.payments.map((p, i) => (
              <div key={p.id} className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: p.incentivePaid ? '#16a34a' : '#e5e7eb' }} />
            ))}
          </div>
        </div>
        {/* Amounts */}
        <div className="text-right flex-shrink-0">
          <p className="font-black text-sm" style={{ color: hasOutstanding ? '#d97706' : '#16a34a' }}>
            {hasOutstanding ? `₹${row.outstanding.toLocaleString('en-IN')} due` : '✓ Clear'}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">of {fmtINR(row.totalEarned)}</p>
        </div>
        <span className="text-gray-300 text-sm ml-1">›</span>
      </div>
    </button>
  );
};

/* ── Student Coordinators ─────────────────────────────────────────────────────
   Students converted after a coordinator meeting: 10% of the before-placement
   fee to the coordinator once it is fully paid (no salesperson incentive on
   them). The admin marks each one paid out. */
const CoordinatorIncentives: React.FC<{ adminPassword: string }> = ({ adminPassword }) => {
  const [data, setData] = useState<MeetIncentiveData | null>(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { setData(await meet.incentive({ adminPassword })); } catch (err: any) { toast.error(err.message); }
  }, [adminPassword]);
  useEffect(() => { load(); }, [load]);
  const mark = async (r: IncentiveRow) => {
    setBusy(r.id);
    try {
      await meet.markPaid(adminPassword, r.id, r.paidOut);
      toast.success(r.paidOut ? 'Marked as not paid out' : `Paid out to ${r.coordinatorName}`);
      await load();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(''); }
  };
  if (!data) return null;
  return (
    <div className="space-y-3">
      <div className="px-1">
        <h2 className="text-base font-black text-gray-800">Student Coordinators</h2>
        <p className="text-xs text-gray-400">{data.pct}% of the before-placement fee for students converted after their meeting, once fully paid</p>
      </div>
      {data.rows.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-2xl border border-gray-100 text-sm text-gray-400">No students converted after a coordinator meeting yet</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(data.coordinators || []).map(c => (
              <div key={c.id} className="bg-white rounded-xl border border-gray-100 px-3 py-2">
                <p className="font-bold text-gray-800 text-sm truncate">{c.name}</p>
                <p className="text-[11px] text-gray-400">{c.students} student{c.students === 1 ? '' : 's'}</p>
                <p className="text-sm font-black mt-1" style={{ color: c.toBePaid > 0 ? '#d97706' : '#16a34a' }}>{c.toBePaid > 0 ? `${fmtINR(c.toBePaid)} due` : '✓ Clear'}</p>
                <p className="text-[10px] text-gray-400">{fmtINR(c.earned)} earned{c.waiting ? ` · ${fmtINR(c.waiting)} waiting` : ''}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <IncentiveRows rows={data.rows} onMark={mark} busy={busy} />
          </div>
        </>
      )}
    </div>
  );
};

/* ── Main page ─────────────────────────────────────────────────────────────── */
const ANIM = `
@keyframes imSlideIn { from{transform:translateX(100%);opacity:0} to{transform:translateX(0);opacity:1} }
@keyframes imFadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
.im-fadeup { animation: imFadeUp .3s ease both; }
`;

const IncentiveMonitor: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [data, setData]       = useState<IncentiveSpRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer]   = useState<IncentiveSpRow | null>(null);
  const [filter, setFilter]   = useState<'all' | 'outstanding' | 'cleared'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIncentiveMonitor(adminPassword);
      setData(res.data);
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = (updated: IncentiveSpRow) => {
    setData(prev => prev.map(r => r.salesPerson === updated.salesPerson ? updated : r));
    if (drawer?.salesPerson === updated.salesPerson) setDrawer(updated);
  };

  const totalEarned      = data.reduce((s, r) => s + r.totalEarned, 0);
  const totalGiven       = data.reduce((s, r) => s + r.totalPaid, 0);
  const totalOutstanding = data.reduce((s, r) => s + r.outstanding, 0);

  const shown = data.filter(r =>
    filter === 'all' ? true : filter === 'outstanding' ? r.outstanding > 0 : r.outstanding === 0
  );

  return (
    <TrackerLayout>
      <style>{ANIM}</style>
      <div className="space-y-5 max-w-2xl">

        {/* Header */}
        <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg,#1e1433,#312741)' }}>
          <h1 className="text-xl font-black text-white mb-1">Incentive Monitor</h1>
          <p className="text-white/50 text-sm mb-4">Track and mark incentive payouts to salespeople</p>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Total Earned', val: totalEarned,      color: '#fff' },
              { label: 'Given',        val: totalGiven,        color: '#86efac' },
              { label: 'Outstanding',  val: totalOutstanding,  color: totalOutstanding > 0 ? '#fde68a' : '#86efac' },
            ].map(s => (
              <div key={s.label} className="bg-white/10 rounded-xl p-3 text-center">
                <p className="font-black text-lg" style={{ color: s.color }}>
                  {fmtINR(s.val)}
                </p>
                <p className="text-white/50 text-[11px] font-semibold mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2">
          {([['all','All SPs'], ['outstanding','Outstanding ⚠'], ['cleared','Cleared ✓']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              className="px-4 py-2 rounded-full text-xs font-bold border-2 transition-all"
              style={{
                background: filter===id ? '#ede9fe' : '#fff',
                color: '#7c3aed',
                borderColor: filter===id ? '#7c3aed' : '#e5e7eb',
              }}>
              {label}
            </button>
          ))}
          <button onClick={load} className="ml-auto px-3 py-2 rounded-full text-xs font-bold border-2 border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
            ↻ Refresh
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
          </div>
        ) : shown.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <p className="text-4xl mb-3">{filter === 'cleared' ? '🎉' : '💰'}</p>
            <p className="text-gray-500 font-semibold">
              {filter === 'cleared' ? 'No cleared SPs yet' : filter === 'outstanding' ? 'No outstanding incentives!' : 'No incentive data yet'}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {filter === 'outstanding' ? 'All incentives have been given' : 'Fully Paid students with a SP assigned will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {shown.map((row, i) => (
              <div key={row.salesPerson} className="im-fadeup" style={{ animationDelay: `${i * 40}ms` }}>
                <SpCard row={row} onClick={() => setDrawer(row)} />
              </div>
            ))}
          </div>
        )}

        {!adminPassword.startsWith('COORD:') && <CoordinatorIncentives adminPassword={adminPassword} />}
      </div>

      {/* Drawer */}
      {drawer && (
        <SpDrawer
          row={drawer}
          adminPassword={adminPassword}
          onClose={() => setDrawer(null)}
          onUpdate={handleUpdate}
        />
      )}
    </TrackerLayout>
  );
};

export default IncentiveMonitor;
